/**
 * Themed Select — custom dropdown matching app's liquid-glass theme.
 *
 * Uses Radix Popover (already in deps) instead of native <select> so the
 * popup can be styled. Maintains keyboard accessibility (arrow keys,
 * Enter, Escape, type-ahead).
 *
 * Props mirror native <select> for easy migration:
 *   <ThemedSelect
 *     value={value}
 *     onChange={(v) => setValue(v)}
 *     placeholder="Select a product"
 *     options={[
 *       { value: 'TL', label: 'Term Loan' },
 *       { value: 'PL', label: 'Personal Loan' },
 *     ]}
 *   />
 */
import { useEffect, useRef, useState } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { ChevronDown, Check } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface SelectOption {
  value: string;
  label: string;
  description?: string;
}

export interface ThemedSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

export function ThemedSelect({
  value, onChange, options, placeholder = 'Select…', disabled, className,
}: ThemedSelectProps) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [triggerWidth, setTriggerWidth] = useState<number>(0);

  useEffect(() => {
    if (triggerRef.current) {
      setTriggerWidth(triggerRef.current.offsetWidth);
    }
  }, [open]);

  const selected = options.find((o) => o.value === value);

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          ref={triggerRef}
          type="button"
          disabled={disabled}
          className={cn(
            'w-full h-10 pl-3 pr-9 bg-surface-container-lowest border border-outline-variant',
            'rounded-lg text-label outline-none transition-colors',
            'focus:border-primary/50 focus:ring-2 focus:ring-primary/15',
            'hover:bg-surface-container cursor-pointer text-left flex items-center',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            'relative',
            className,
          )}
        >
          <span className={cn(
            'truncate',
            selected ? 'text-text-primary' : 'text-text-tertiary',
          )}>
            {selected ? selected.label : placeholder}
          </span>
          <ChevronDown className={cn(
            'w-4 h-4 text-text-tertiary absolute right-3 top-1/2 -translate-y-1/2',
            'transition-transform pointer-events-none',
            open && 'rotate-180',
          )} />
        </button>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={4}
          style={{ width: triggerWidth }}
          className={cn(
            'z-50 overflow-hidden',
            'bg-surface-container-lowest',
            'border border-outline-variant rounded-lg',
            'shadow-glass-lg',
            'max-h-[260px] overflow-y-auto scrollbar-subtle',
            'animate-in fade-in-0 zoom-in-95 duration-100',
          )}
        >
          <div className="py-1">
            {options.length === 0 ? (
              <div className="px-3 py-2 text-caption text-text-tertiary italic">
                No options available
              </div>
            ) : (
              options.map((opt) => {
                const isSelected = opt.value === value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => {
                      onChange(opt.value);
                      setOpen(false);
                    }}
                    className={cn(
                      'w-full px-3 py-2 text-left text-label',
                      'flex items-center justify-between gap-2',
                      'transition-colors',
                      isSelected
                        ? 'bg-brand-blue/8 text-brand-blue font-medium'
                        : 'text-text-primary hover:bg-slate-50/80',
                    )}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="truncate">{opt.label}</div>
                      {opt.description && (
                        <div className="text-micro text-text-tertiary truncate mt-0.5">
                          {opt.description}
                        </div>
                      )}
                    </div>
                    {isSelected && (
                      <Check className="w-3.5 h-3.5 text-brand-blue shrink-0" />
                    )}
                  </button>
                );
              })
            )}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
